import { Address, MapBoxFeature, Option } from '@/types/mapbox';

export const MAPBOX_FORWARD_GEOCODING_URL = 'https://api.mapbox.com/search/geocode/v6/forward';
export const MAPBOX_REVERSE_GEOCODING_URL = 'https://api.mapbox.com/search/geocode/v6/reverse';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAP_API_TOKEN;

const getFeatureLabel = (feature: MapBoxFeature): string =>
  feature?.properties?.full_address || feature?.properties?.name || feature?.properties?.name_preferred || '';

export const mapboxFeatureToOption = (feature: MapBoxFeature): Option<Address> => {
  if (!feature) {
    return null;
  }

  const address: Address = {
    country_code: feature.properties.context.country?.country_code,
    full_address: feature.properties.full_address,
    location: {
      latitude: feature.properties.coordinates.latitude,
      longitude: feature.properties.coordinates.longitude,
    },
    country: feature.properties.context.country?.name,
    state_province: feature.properties.context.region?.name,
    city: feature.properties.context.place?.name,
    street: feature.properties.context.street?.name,
    post_code: feature.properties.context.postcode?.name,
  };

  return {
    value: address,
    label: getFeatureLabel(feature),
  };
};

export const formatMapboxFeatures = (features: MapBoxFeature[] = []) => {
    const options = features.map(mapboxFeatureToOption);
    return options.filter((option) => option.label);
};

export const addressToOption = (address: Address): Option<Address> => {
  return {
    value: address,
    label: address.full_address,
  };
};

/**
 * Обогащает адрес данными из Reverse Geocoding (например, postcode)
 * Используется когда Forward Geocoding не возвращает postcode для городов
 */
export const enrichAddressWithReverseGeocode = async (
  option: Option<Address>,
  lang: string = 'ru'
): Promise<Option<Address>> => {
  if (!option?.value?.location || !MAPBOX_ACCESS_TOKEN) {
    return option;
  }

  // Если postcode уже есть, не делаем дополнительный запрос
  if (option.value.post_code) {
    return option;
  }

  const { longitude, latitude } = option.value.location;

  try {
    const params = new URLSearchParams({
      longitude: String(longitude),
      latitude: String(latitude),
      access_token: MAPBOX_ACCESS_TOKEN,
      language: lang,
    });

    const response = await fetch(`${MAPBOX_REVERSE_GEOCODING_URL}?${params.toString()}`);
    const result = await response.json();

    if (result?.features?.length > 0) {
      const feature = result.features[0];
      const postcode = feature?.properties?.context?.postcode?.name;

      if (postcode) {
        return {
          ...option,
          value: {
            ...option.value,
            post_code: postcode,
          },
        };
      }
    }
  } catch (error) {
    console.warn('Failed to enrich address with reverse geocode:', error);
  }

  return option;
};
